import * as React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { usePostRouteTimedShuttleStopsMutation, useGetRouteTimedShuttleByTrackIdQuery } from "../redux/route-timed-shuttle-api";
import { skipToken } from '@reduxjs/toolkit/query';
import type { PostRouteTimedShuttleStopsResponseDto } from "../redux/route-timed-shuttle-api";
import { useSelectedSpace } from "@src/features/space-home/hooks/use-selected-space";
import { urlForRouteTimedShuttlesStops } from "../route-timed-shuttle-api-urls";

interface LatLng { lat: number; lng: number; }
interface Stop extends LatLng { name: string; eta?: string; }

export function RouteTimedShuttleMapBuilder() {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<L.Map>();
  const polylineRef = React.useRef<L.Polyline>();
  const [route, setRoute] = React.useState<LatLng[]>([]);
  const [stops, setStops] = React.useState<Stop[]>([]);
  const [mode, setMode] = React.useState<'route' | 'stop'>('stop');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [pendingStop, setPendingStop] = React.useState<LatLng | null>(null);
  const [stopName, setStopName] = React.useState("");
  const [stopEta, setStopEta] = React.useState("");
  const [loadingRoute, setLoadingRoute] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<{display_name: string, lat: string, lon: string}[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const selectedSpace = useSelectedSpace();
  const [postRouteTimedShuttleStops] = usePostRouteTimedShuttleStopsMutation<PostRouteTimedShuttleStopsResponseDto>();
  const [postResult, setPostResult] = React.useState<string | null>(null);
  const [trackName, setTrackName] = React.useState("");
  const [distanceMeters, setDistanceMeters] = React.useState<number | null>(null);
  const [durationSeconds, setDurationSeconds] = React.useState<number | null>(null);
  const [posting, setPosting] = React.useState(false);
  const [loadModalOpen, setLoadModalOpen] = React.useState(false);
  const [loadTrackId, setLoadTrackId] = React.useState("");
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const shouldFetchRoute = loadModalOpen && !!loadTrackId.trim() && !!selectedSpace;
  const { data: loadedRoute, isFetching: loadingRouteData, error: loadRouteError, refetch } = useGetRouteTimedShuttleByTrackIdQuery(
    shouldFetchRoute ? { spaceId: selectedSpace.spaceId, trackId: loadTrackId.trim() } : skipToken,
    { skip: !shouldFetchRoute, refetchOnMountOrArgChange: true }
  );

  React.useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [32.0853, 34.7818], // Tel Aviv default
      zoom: 13,
    });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "",
    }).addTo(map);

    polylineRef.current = L.polyline(route, { color: "blue" }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      const latlng = e.latlng;
      if (mode === 'stop') {
        setPendingStop({ lat: latlng.lat, lng: latlng.lng });
        setModalOpen(true);
      } else {
        setRoute((prev) => [...prev, { lat: latlng.lat, lng: latlng.lng }]);
      }
    });

    return () => {
      map.off();
      map.remove();
    };
    // eslint-disable-next-line
  }, [mode]);

  React.useEffect(() => {
    if (polylineRef.current) {
      polylineRef.current.setLatLngs(route);
    }
    if (mapRef.current) {
      mapRef.current.eachLayer((layer: any) => {
        if (layer instanceof L.Marker && layer._isStopMarker) {
          mapRef.current?.removeLayer(layer);
        }
      });
      stops.forEach((stop) => {
        const marker = L.marker([stop.lat, stop.lng]).addTo(mapRef.current!);
        (marker as any)._isStopMarker = true;
        marker.bindTooltip(stop.name + (stop.eta ? ` (ETA: ${stop.eta})` : ""), { permanent: true, direction: "top" });
      });
    }
  }, [route, stops]);

  React.useEffect(() => {
    if (search.length < 3) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const timeout = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&accept-language=he&q=${encodeURIComponent(search)}`)
        .then(res => res.json())
        .then(data => setSearchResults(data))
        .finally(() => setSearchLoading(false));
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  function handleModalSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pendingStop && stopName.trim()) {
      setStops((prev) => [...prev, { ...pendingStop, name: stopName.trim(), eta: stopEta.trim() || undefined }]);
      setModalOpen(false);
      setPendingStop(null);
      setStopName("");
      setStopEta("");
    }
  }

  function handleModalClose() {
    setModalOpen(false);
    setPendingStop(null);
    setStopName("");
    setStopEta("");
  }

  async function handleAutoRoute() {
    if (stops.length < 2) return;
    setLoadingRoute(true);
    const coords = stops.map(s => `${s.lng},${s.lat}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data.routes && data.routes[0]) {
        const points = data.routes[0].geometry.coordinates.map(([lng, lat]: [number, number]) => ({ lat, lng }));
        setRoute(points);
        setDistanceMeters(data.routes[0].distance);
        setDurationSeconds(data.routes[0].duration);
      } else {
        alert('Could not calculate route.');
        setDistanceMeters(null);
        setDurationSeconds(null);
      }
    } catch (e) {
      alert('Error calculating route.');
      setDistanceMeters(null);
      setDurationSeconds(null);
    } finally {
      setLoadingRoute(false);
    }
  }

  function removeStop(idx: number) {
    setStops(stops => stops.filter((_, i) => i !== idx));
  }

  function moveStop(idx: number, dir: -1 | 1) {
    setStops(stops => {
      const newStops = [...stops];
      const target = idx + dir;
      if (target < 0 || target >= stops.length) return stops;
      [newStops[idx], newStops[target]] = [newStops[target], newStops[idx]];
      return newStops;
    });
  }

  function handleSearchSelect(result: {display_name: string, lat: string, lon: string}) {
    setPendingStop({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
    setModalOpen(true);
    setSearch("");
    setSearchResults([]);
  }

  async function handlePostStops() {
    setPostResult(null);
    setPosting(true);
    if (!selectedSpace) {
      setPostResult("No space selected.");
      setPosting(false);
      return;
    }
    if (!trackName.trim()) {
      setPostResult("Please enter a track name.");
      setPosting(false);
      return;
    }
    try {
      const result = await postRouteTimedShuttleStops({
        spaceId: selectedSpace.spaceId,
        body: {
          route: route.map(({ lat, lng }) => [lat, lng]),
          stops,
          trackName: trackName.trim(),
          distanceMeters: distanceMeters ?? undefined,
          durationSeconds: durationSeconds ?? undefined,
        },
      }).unwrap();
      if (result && result.trackId) {
        setPostResult(`Posted successfully! Track ID: ${result.trackId}`);
      } else {
        setPostResult("Posted successfully!");
      }
    } catch {
      setPostResult("Failed to post stops.");
    } finally {
      setPosting(false);
    }
  }

  React.useEffect(() => {
    if (loadedRoute && loadModalOpen) {
      setRoute(loadedRoute.route.map(([lat, lng]: [number, number]) => ({ lat, lng })));
      setStops(loadedRoute.stops);
      setTrackName(loadedRoute.trackName || "");
      setDistanceMeters(loadedRoute.distanceMeters ?? null);
      setDurationSeconds(loadedRoute.durationSeconds ?? null);
      setLoadModalOpen(false);
      setLoadTrackId("");
      setLoadError(null);
    } else if (loadRouteError && loadModalOpen) {
      setLoadError("Failed to load route. Check the Track ID.");
    }
    // eslint-disable-next-line
  }, [loadedRoute, loadRouteError]);

  if (!selectedSpace) {
    return <div className="text-red-600 font-bold">Error: No space selected. Please access this page from a valid dashboard route.</div>;
  }

  return (
    <div>
      <div className="mb-2 flex gap-2">

        <button
          className={`px-3 py-1 rounded ${mode === 'stop' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setMode('stop')}
        >Add Stops</button>
        <button
          className="px-3 py-1 rounded bg-purple-500 text-white"
          onClick={() => setLoadModalOpen(true)}
        >Load Route</button>
      </div>
      <div className="mb-2 text-sm text-gray-600">
        {mode === 'route'
          ? 'Click to add route points.'
          : 'Click to add a stop and fill in details.'}
      </div>
      <div ref={containerRef} style={{ height: 600, width: "100%" }} />
      <div className="mt-4">
        <label className="block mb-1 font-medium">Track Name</label>
        <input
          className="border rounded px-2 py-1 w-full mb-2"
          value={trackName}
          onChange={e => setTrackName(e.target.value)}
          placeholder="Enter track name..."
        />
        {distanceMeters !== null && durationSeconds !== null && (
          <div className="mb-2 text-sm text-gray-700">
            <span>Distance: {(distanceMeters / 1000).toFixed(2)} km</span>{" | "}
            <span>Duration: {Math.round(durationSeconds / 60)} min</span>
          </div>
        )}
        {mode === 'stop' && (
        <div className="mb-2">
          <input
            className="border rounded px-2 py-1 w-full"
            placeholder="Search address to add stop..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {searchLoading && <div className="text-xs text-gray-500 mt-1">Searching...</div>}
          {searchResults.length > 0 && (
            <ul className="border rounded bg-white mt-1 max-h-40 overflow-auto z-20 relative">
              {searchResults.map((r, i) => (
                <li
                  key={i}
                  className="px-2 py-1 hover:bg-blue-100 cursor-pointer text-sm"
                  onClick={() => handleSearchSelect(r)}
                >
                  {r.display_name}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
        <strong>Stops:</strong>
        <ul>
          {stops.map((stop, i) => (
            <li key={i} className="flex items-center gap-2 mb-1">
              <span>{stop.name} ({stop.lat.toFixed(5)}, {stop.lng.toFixed(5)}){stop.eta ? ` - ETA: ${stop.eta}` : ''}</span>
              <button className="text-xs px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300" onClick={() => moveStop(i, -1)} disabled={i === 0}>↑</button>
              <button className="text-xs px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300" onClick={() => moveStop(i, 1)} disabled={i === stops.length - 1}>↓</button>
              <button className="text-xs px-2 py-0.5 bg-red-500 text-white rounded hover:bg-red-600" onClick={() => removeStop(i)}>Remove</button>
            </li>
          ))}
        </ul>
        <button
          className="mt-2 px-3 py-1 bg-green-600 text-white rounded disabled:bg-gray-300 flex items-center gap-2"
          onClick={handleAutoRoute}
          disabled={stops.length < 2 || loadingRoute}
        >
          {loadingRoute && <span className="loader border-2 border-t-2 border-white border-t-green-600 rounded-full w-4 h-4 animate-spin"></span>}
          Auto-calculate route through stops
        </button>
        <button
          className="mt-2 ml-2 px-3 py-1 bg-blue-600 text-white rounded disabled:bg-gray-300 flex items-center gap-2"
          onClick={handlePostStops}
          disabled={posting || stops.length === 0 || route.length === 0}
        >
          {posting && <span className="loader border-2 border-t-2 border-white border-t-blue-600 rounded-full w-4 h-4 animate-spin"></span>}
          Post Stops
        </button>
        {postResult && <div className="mt-2 text-sm">{postResult}</div>}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40" style={{zIndex: 10000}}>
          <form className="bg-white p-6 rounded shadow-md min-w-[300px]" style={{zIndex: 10001, position: 'relative'}} onSubmit={handleModalSubmit}>
            <h2 className="text-lg font-bold mb-2">Add Stop</h2>
            <div className="mb-2">
              <label className="block text-sm font-medium">Stop Name<span className="text-red-500">*</span></label>
              <input
                className="border rounded px-2 py-1 w-full"
                value={stopName}
                onChange={e => setStopName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium">Estimated Arrival Time (optional)</label>
              <input
                className="border rounded px-2 py-1 w-full"
                value={stopEta}
                onChange={e => setStopEta(e.target.value)}
                placeholder="e.g. 08:30"
              />
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" className="px-3 py-1 bg-gray-200 rounded" onClick={handleModalClose}>Cancel</button>
              <button type="submit" className="px-3 py-1 bg-blue-500 text-white rounded">Add Stop</button>
            </div>
          </form>
        </div>
      )}

      {loadModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40" style={{zIndex: 10000}}>
          <form className="bg-white p-6 rounded shadow-md min-w-[300px]" style={{zIndex: 10001, position: 'relative'}} onSubmit={e => { e.preventDefault(); refetch(); }}>
            <h2 className="text-lg font-bold mb-2">Load Route by Track ID</h2>
            <div className="mb-2">
              <label className="block text-sm font-medium">Track ID<span className="text-red-500">*</span></label>
              <input
                className="border rounded px-2 py-1 w-full"
                value={loadTrackId}
                onChange={e => setLoadTrackId(e.target.value)}
                required
                autoFocus
              />
            </div>
            {loadError && <div className="text-red-600 text-sm mb-2">{loadError}</div>}
            <div className="flex gap-2 justify-end mt-4">
              <button type="button" className="px-3 py-1 bg-gray-200 rounded" onClick={() => setLoadModalOpen(false)}>Cancel</button>
              <button type="submit" className="px-3 py-1 bg-purple-500 text-white rounded" disabled={loadingRouteData}>{loadingRouteData ? "Loading..." : "Done"}</button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .loader {
          border-top-color: #16a34a;
          border-right-color: #fff;
          border-bottom-color: #fff;
          border-left-color: #fff;
        }
      `}</style>
    </div>
  );
}
